/**
 * Roof Master — Column Registry (Ship 1 of the editable-grid build).
 *
 * Single source of truth for every column Roof Master can show. Drives:
 *   1. which `deals` columns the table SELECTs (derived from here — no drift)
 *   2. how each cell renders + whether it's editable (EditableCell reads `type`)
 *   3. the financial/role gate (gated columns hidden from non-managers)
 *
 * Ships 2 (column builder) and 3 (custom fields) extend THIS registry — a
 * saved-layout row will reference these keys, and custom fields will append
 * runtime ColumnDef entries backed by deals.custom_fields JSONB.
 *
 * Editable set is Robert's lane (claims + build phases): pre-inspection,
 * adjuster meetings, claims context, reinspections/repair attempts, builds-to-
 * calendar, punch lists, warranties. Per Greg (Jun 24): crew pay + material
 * cost ARE shown to him (gated to managers); revenue/credits/scope-revenue/
 * collections are never surfaced here at all.
 */

export type CellType =
  | "text"
  | "number"
  | "currency"
  | "date" // date-only (YYYY-MM-DD)
  | "datetime" // fake-UTC wall-clock (see helpers below)
  | "boolean"
  | "select"
  | "readonly"
  | "special"; // rendered by RoofMasterTable itself (DealLink, status→stage, etc.)

export interface SelectOption {
  value: string;
  label: string;
}

export interface ColumnDef {
  /** `deals` column name, or a computed key (e.g. `_status`) for special cells. */
  key: string;
  label: string;
  group: ColumnGroup;
  type: CellType;
  editable: boolean;
  /** Manager-only to view (crew pay / material cost). */
  gated?: boolean;
  /** px width hint for the column. */
  width?: number;
  /** Static option list for `select` cells (enums). Roster-backed selects are
   *  resolved at render time from team_roster, not here. */
  options?: SelectOption[];
  /** Help text shown as the cell title attribute. */
  hint?: string;
  /** True for user-defined custom fields (value lives in deals.custom_fields). */
  custom?: boolean;
  /** For custom columns: the key inside deals.custom_fields (col.key is `cf:<cfKey>`). */
  cfKey?: string;
}

export type ColumnGroup =
  | "Account"
  | "Pre-Inspection"
  | "Adjuster"
  | "Claims"
  | "Reinspection"
  | "Build"
  | "Punch List"
  | "Warranty"
  | "Custom";

/* A user-defined custom field (row from custom_field_definitions). */
export interface CustomFieldDef {
  id: string;
  key: string;
  label: string;
  field_type: "text" | "number" | "currency" | "date" | "datetime" | "boolean" | "select";
  options?: SelectOption[];
  sort_order?: number;
}

/** Convert active custom-field definitions into ColumnDefs (group "Custom",
 *  always editable, namespaced key `cf:<key>`). */
export function customFieldsToColumns(defs: CustomFieldDef[]): ColumnDef[] {
  return defs.map((d) => ({
    key: `cf:${d.key}`,
    label: d.label,
    group: "Custom" as ColumnGroup,
    type: d.field_type,
    editable: true,
    width: 150,
    options: d.field_type === "select" ? (d.options ?? []) : undefined,
    custom: true,
    cfKey: d.key,
  }));
}

/* ── Enum option lists (from pg enums, Jun 24 2026) ───────────────── */
export const REINSPECTION_STATUS_OPTIONS: SelectOption[] = [
  { value: "pending_review", label: "Pending review" },
  { value: "approved_for_reinspection", label: "Approved for reinspection" },
  { value: "reinspection_scheduled", label: "Reinspection scheduled" },
  { value: "reinspection_complete", label: "Reinspection complete" },
  { value: "supplemental_claim_filed", label: "Supplemental claim filed" },
  { value: "resolved_approved", label: "Resolved — approved" },
  { value: "resolved_denied", label: "Resolved — denied" },
];

export const WARRANTY_STATUS_OPTIONS: SelectOption[] = [
  { value: "pending_registration", label: "Pending registration" },
  { value: "registered", label: "Registered" },
  { value: "active", label: "Active" },
  { value: "claim_filed", label: "Claim filed" },
  { value: "in_progress", label: "In progress" },
  { value: "claim_approved", label: "Claim approved" },
  { value: "claim_denied", label: "Claim denied" },
  { value: "voided", label: "Voided" },
  { value: "expired", label: "Expired" },
];

/* Roles allowed to see gated (crew $ / cost) columns. Robert is
   roof_team_manager. Field RMs do not see dollar figures. */
export const GATED_VIEW_ROLES = new Set<string>([
  "roof_team_manager",
  "roof_director",
  "director_of_operations",
  "admin",
  "asm",
  "sales_director",
  "finance",
]);

/* ────────────────────────────────────────────────────────────────
   THE REGISTRY
   Order here = default left-to-right column order (Ship 2 lets Robert
   reorder + toggle; for now this is the shared layout).
   ──────────────────────────────────────────────────────────────── */
export const ROOF_MASTER_COLUMNS: ColumnDef[] = [
  /* Account / identity — special-rendered or light edit */
  { key: "homeowner_name", label: "Account Name", group: "Account", type: "special", editable: false, width: 170 },
  { key: "_status", label: "Status", group: "Account", type: "special", editable: false, width: 130 },
  { key: "_address", label: "Address", group: "Account", type: "special", editable: false, width: 220 },
  { key: "homeowner_phone", label: "Phone", group: "Account", type: "text", editable: true, width: 130 },
  { key: "homeowner_phone_alt", label: "Alt Phone", group: "Account", type: "text", editable: true, width: 130 },
  { key: "_owner", label: "Owner", group: "Account", type: "special", editable: false, width: 130 },

  /* Pre-Inspection */
  { key: "assigned_rs_id", label: "RS", group: "Pre-Inspection", type: "special", editable: false, width: 150 },
  { key: "pre_inspection_date", label: "PRE Date", group: "Pre-Inspection", type: "datetime", editable: false, width: 165, hint: "Scheduled via the 🗓️ action — editing the raw date would skip the scheduling workflow" },
  { key: "pre_inspection_completed", label: "PRE Done", group: "Pre-Inspection", type: "boolean", editable: true, width: 80 },
  { key: "pre_inspection_findings", label: "PRE Findings", group: "Pre-Inspection", type: "text", editable: true, width: 220 },

  /* Adjuster */
  { key: "adjuster_meeting_date", label: "Adj Meeting", group: "Adjuster", type: "special", editable: false, width: 175 },
  { key: "adjuster_name", label: "Adjuster", group: "Adjuster", type: "text", editable: true, width: 140 },
  { key: "adjuster_phone", label: "Adj Phone", group: "Adjuster", type: "text", editable: true, width: 130 },
  { key: "adjuster_present", label: "Adj Present", group: "Adjuster", type: "boolean", editable: true, width: 90 },
  { key: "la_company", label: "LA Company", group: "Adjuster", type: "text", editable: true, width: 130 },
  { key: "adjuster_meeting_outcome", label: "Meeting Outcome", group: "Adjuster", type: "text", editable: true, width: 220 },

  /* Claims */
  { key: "insurance_company", label: "Insurance", group: "Claims", type: "text", editable: true, width: 150 },
  { key: "claim_number", label: "Claim #", group: "Claims", type: "text", editable: true, width: 130 },
  { key: "claim_called_in_date", label: "Claim Called In", group: "Claims", type: "date", editable: true, width: 140 },
  { key: "claim_decision", label: "Claim Decision", group: "Claims", type: "text", editable: true, width: 140 },
  { key: "claims_rep_name", label: "Claims Rep", group: "Claims", type: "text", editable: true, width: 140 },
  { key: "claims_rep_phone", label: "Rep Phone", group: "Claims", type: "text", editable: true, width: 130 },
  { key: "claim_notes", label: "Claim Notes", group: "Claims", type: "text", editable: true, width: 240 },

  /* Reinspection / repair attempts */
  { key: "reinspection_status", label: "Reinspect Status", group: "Reinspection", type: "select", editable: true, width: 175, options: REINSPECTION_STATUS_OPTIONS },
  { key: "reinspection_reason", label: "Reinspect Reason", group: "Reinspection", type: "text", editable: true, width: 200 },
  { key: "reinspection_scheduled_date", label: "Reinspect Scheduled", group: "Reinspection", type: "datetime", editable: false, width: 175, hint: "Scheduled via the reinspection workflow — view only here" },
  { key: "reinspection_filed_date", label: "Reinspect Filed", group: "Reinspection", type: "date", editable: true, width: 140 },
  { key: "reinspection_result", label: "Reinspect Result", group: "Reinspection", type: "text", editable: true, width: 180 },
  { key: "reinspection_partial_approval", label: "Partial Approval", group: "Reinspection", type: "boolean", editable: true, width: 90 },

  /* Build / calendar */
  { key: "ho_preferred_build_date", label: "HO Preferred Build", group: "Build", type: "date", editable: true, width: 150 },
  // READ-ONLY on purpose: build scheduling runs through the 🗓️ ScheduleBuildModal
  // (creates the calendar event) and is gated on Chad's approval (schedule_build
  // stage). A raw editable cell here would bypass both — a Chad-approval bypass.
  { key: "build_scheduled_date", label: "Build Scheduled", group: "Build", type: "date", editable: false, width: 140, hint: "Scheduled via the 🗓️ action after Chad's approval — view only here" },
  // Oct 26, 2026: the last day of a build that runs more than one day (blank = one day). Read-only for the same reason.
  { key: "build_end_date", label: "Build Last Day", group: "Build", type: "date", editable: false, width: 130, hint: "Set with Schedule build / Reschedule build on the PM Board — blank means a one-day build" },
  { key: "build_completed_date", label: "Build Completed", group: "Build", type: "date", editable: true, width: 140 },
  { key: "crew_name", label: "Crew", group: "Build", type: "text", editable: true, width: 130 },
  { key: "crew_size", label: "Crew Size", group: "Build", type: "number", editable: true, width: 90 },
  { key: "installed_squares", label: "Squares", group: "Build", type: "number", editable: true, width: 90 },
  { key: "shingle_color", label: "Shingle Color", group: "Build", type: "text", editable: true, width: 130 },
  { key: "material_supplier", label: "Material Supplier", group: "Build", type: "text", editable: true, width: 150 },
  { key: "material_order_date", label: "Material Ordered", group: "Build", type: "date", editable: true, width: 140 },
  { key: "material_delivery_date", label: "Material Delivery", group: "Build", type: "date", editable: true, width: 140 },
  { key: "material_order_confirmed", label: "Material Confirmed", group: "Build", type: "boolean", editable: true, width: 90 },
  { key: "material_cost", label: "Material Cost", group: "Build", type: "currency", editable: true, gated: true, width: 120 },
  { key: "total_crew_pay", label: "Crew Pay", group: "Build", type: "currency", editable: true, gated: true, width: 120 },
  { key: "build_notes", label: "Build Notes", group: "Build", type: "text", editable: true, width: 240 },

  /* Punch List */
  { key: "punch_list_complete", label: "Punch Complete", group: "Punch List", type: "boolean", editable: true, width: 90 },

  /* Warranty */
  { key: "warranty_status", label: "Warranty Status", group: "Warranty", type: "select", editable: true, width: 170, options: WARRANTY_STATUS_OPTIONS },
  { key: "warranty_type", label: "Warranty Type", group: "Warranty", type: "text", editable: true, width: 140 },
  { key: "warranty_registered_date", label: "Warranty Registered", group: "Warranty", type: "date", editable: true, width: 150 },
  { key: "warranty_expiration_date", label: "Warranty Expires", group: "Warranty", type: "date", editable: true, width: 150 },
  { key: "oc_warranty_number", label: "OC Warranty #", group: "Warranty", type: "text", editable: true, width: 140 },
];

/* Extra deals columns the table needs loaded but doesn't render as its own
   column — consumed by special cells (status/address/RS), the Pics/Hover
   indicators, DispatchQuestionPill routing, filters, and gating. Registry
   columns are already selected; this is only the non-registry support set. */
const EXTRA_SELECT_COLS = [
  "id", "stage", "phase", "owner_id", "split_rm_id", "pre_inspection_assigned_to",
  "property_street", "property_city", "property_state", "property_zip",
  "hover_link", "imging_report_link", "custom_fields",
  // Oct 2, 2026 — homeowner add-on notes for the Schedule Build modal.
  "upgrades", "retail_profit", "at_cost_retail", "upgrades_description", "upgrades_internal_note", "retail_profit_description", "retail_profit_internal_note", "at_cost_retail_description", "at_cost_retail_internal_note", "pre_inspection_data", "pre_inspection_findings", "pre_inspection_completed",
];

/** The exact column list to SELECT from `deals` — derived from the registry
 *  (real deals columns only; computed `_`-prefixed keys excluded) plus extras.
 *  Deduped. */
export const ROOF_MASTER_SELECT = Array.from(
  new Set([
    ...ROOF_MASTER_COLUMNS.map((c) => c.key).filter((k) => !k.startsWith("_")),
    ...EXTRA_SELECT_COLS,
  ]),
).join(",");

/* ────────────────────────────────────────────────────────────────
   Date conversion — fake-UTC wall-clock ↔ datetime-local input.
   Meeting/timestamp values are stored as Eastern wall-clock digits tagged
   +00:00 (Zoho migration convention). Read/write the raw digits; never
   construct a Date (would shift by browser TZ offset). Shared so the grid
   and any future reader stay consistent.
   ──────────────────────────────────────────────────────────────── */
export function utcIsoToLocalInput(iso: string | null | undefined): string {
  if (!iso) return "";
  const m = String(iso).match(/^(\d{4}-\d{2}-\d{2})T(\d{2}:\d{2})/);
  return m ? `${m[1]}T${m[2]}` : "";
}
export function localInputToUtcIso(input: string): string | null {
  if (!input) return null;
  const m = input.match(/^(\d{4}-\d{2}-\d{2})T(\d{2}:\d{2})/);
  return m ? `${m[1]}T${m[2]}:00+00:00` : null;
}

/* ────────────────────────────────────────────────────────────────
   Ship 2 — saved shared layout (column builder).
   A layout is an ordered array of LayoutEntry, persisted as the single
   `roof_master_layout.columns` jsonb. Absence of a saved layout = the default
   registry order, everything visible.
   ──────────────────────────────────────────────────────────────── */
export interface LayoutEntry {
  key: string;
  hidden?: boolean;
  w?: number; // width override (px)
  label?: string; // header label override
}

/** Always-first, never-hidden anchor column (the DealLink). */
export const LAYOUT_PINNED_FIRST = "homeowner_name";

export interface OrderedColumn {
  col: ColumnDef;
  hidden: boolean;
}

/**
 * Full ordered column list (including hidden) with layout overrides applied —
 * drives the column manager. The column universe = built-in registry +
 * `customCols` (user-defined fields). Columns missing from a saved layout
 * (newly added registry columns OR new custom fields) are appended visible so
 * nothing silently disappears; stale layout keys (deleted custom fields) are
 * ignored. `homeowner_name` is forced first + visible.
 */
export function orderedColumns(
  layout: LayoutEntry[] | null | undefined,
  customCols: ColumnDef[] = [],
): OrderedColumn[] {
  const universe = [...ROOF_MASTER_COLUMNS, ...customCols];
  const byKey: Record<string, ColumnDef> = Object.fromEntries(universe.map((c) => [c.key, c]));
  const out: OrderedColumn[] = [];
  const seen = new Set<string>();

  const push = (key: string, hidden: boolean, w?: number, label?: string) => {
    const base = byKey[key];
    if (!base || seen.has(key)) return;
    seen.add(key);
    out.push({ col: { ...base, width: w ?? base.width, label: label ?? base.label }, hidden });
  };

  if (layout && layout.length) {
    for (const e of layout) push(e.key, !!e.hidden, e.w, e.label);
  }
  // Append any columns not in the saved layout, visible.
  for (const c of universe) if (!seen.has(c.key)) push(c.key, false);

  // Pin the anchor column first + force visible.
  const idx = out.findIndex((o) => o.col.key === LAYOUT_PINNED_FIRST);
  if (idx > 0) {
    const [pin] = out.splice(idx, 1);
    pin.hidden = false;
    out.unshift(pin);
  } else if (idx === 0) {
    out[0].hidden = false;
  }
  return out;
}

/** Visible, gated-filtered columns for the table itself. */
export function applyLayout(
  layout: LayoutEntry[] | null | undefined,
  canSeeGated: boolean,
  customCols: ColumnDef[] = [],
): ColumnDef[] {
  return orderedColumns(layout, customCols)
    .filter((o) => !o.hidden)
    .filter((o) => !o.col.gated || canSeeGated)
    .map((o) => o.col);
}
